import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoaderIcon, PencilIcon, TrashIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxConfigApi } from '@/api/sandboxConfig.api';
import { Button } from '@/components/ui/button';
import { UserName } from '@/components/UserName';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import type { Sandbox, SandboxTool } from '@/proto/agentplatform/v1/sandbox_pb';
import { MountPointType } from '@/proto/agentplatform/v1/sandbox_pb';
import { create } from '@bufbuild/protobuf';
import { ListSandboxTraceRecordsRequestSchema } from '@/proto/agentplatform/httpv2/sandbox_pb';
import { formatTimestamp } from '@/utils/utils';
import { useSecrets } from '@/pages/credentials/hooks/useSecrets';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-accent rounded transition-colors"
      title="复制"
    >
      {copied ? (
        <svg
          className="w-3.5 h-3.5 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg
          className="w-3.5 h-3.5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
}

function InfoItem({
  label,
  children,
  copyable,
}: {
  label: string;
  children: React.ReactNode;
  copyable?: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <div className="flex-1">{children}</div>
        {copyable && <CopyButton text={copyable} />}
      </div>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

const CpuIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
  </svg>
);

const MemoryIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 19v-3m4 3v-5m4 5v-7m4 7v-9" />
    <rect x="2" y="19" width="20" height="2" rx="1" />
  </svg>
);

const VariableIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 21s-4-3-4-9 4-9 4-9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 3s4 3 4 9-4 9-4 9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18" />
  </svg>
);

const ToolIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
    />
  </svg>
);

const DatabaseIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 5v14c0 1.657 4.03 3 9 3s9-1.343 9-3V5"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12c0 1.657 4.03 3 9 3s9-1.343 9-3" />
  </svg>
);

type TabId = 'overview' | 'tools' | 'trace-records';

function SpecInfo({ instance }: { instance: Sandbox }) {
  const { data: spec } = useQuery({
    queryKey: ['sandbox-spec', instance.sandboxSpecId],
    queryFn: () => sandboxConfigApi.getSandboxSpec(instance.sandboxSpecId!),
    enabled: !!instance.sandboxSpecId,
  });
  if (!spec) return null;
  return (
    <>
      <InfoItem label="名称">
        <span className="font-medium">{spec.name}</span>
      </InfoItem>
      <InfoItem label="描述">
        <span className="text-sm text-muted-foreground">{spec.description || '-'}</span>
      </InfoItem>
    </>
  );
}

function EnvInfo({ instance }: { instance: Sandbox }) {
  const { data: env } = useQuery({
    queryKey: ['sandbox-environment', instance.sandboxEnvironmentId],
    queryFn: () => sandboxConfigApi.getSandboxEnvironment(instance.sandboxEnvironmentId!),
    enabled: !!instance.sandboxEnvironmentId,
  });
  if (!env) return null;
  return (
    <>
      <InfoItem label="名称">
        <span className="font-medium">{env.name}</span>
      </InfoItem>
      <InfoItem label="描述">
        <span className="text-sm text-muted-foreground">{env.description || '-'}</span>
      </InfoItem>
    </>
  );
}

function OverviewTab({ instance }: { instance: Sandbox }) {
  const { data: secretsRes } = useSecrets(1, 100);
  const availableSecretIds = new Set((secretsRes?.data ?? []).map((s) => s.id));
  const secretsLoaded = secretsRes !== undefined;

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            基本信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="实例名称">
              <span className="font-medium">{instance.name}</span>
            </InfoItem>
            <InfoItem label="类型">
              <Badge variant="secondary">{instance.type || '-'}</Badge>
            </InfoItem>
          </div>
          <InfoItem label="描述">
            <span className="text-sm">{instance.description || '暂无描述'}</span>
          </InfoItem>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="实例 ID" copyable={instance.id}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{instance.id}</code>
            </InfoItem>
            <InfoItem label="沙箱模版 ID" copyable={instance.classId}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                {instance.classId || '-'}
              </code>
            </InfoItem>
            <InfoItem label="租户 ID" copyable={instance.tenantId}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {instance.tenantId}
              </code>
            </InfoItem>
            <InfoItem label="创建用户">
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                <UserName userId={instance.userId} />
              </code>
            </InfoItem>
          </div>
        </CardContent>
      </Card>

      {instance.sandboxSpecId && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CpuIcon className="w-4 h-4" />
              资源配置
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <SpecInfo instance={instance} />
            </div>
          </CardContent>
        </Card>
      )}

      {instance.sandboxEnvironmentId && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MemoryIcon className="w-4 h-4" />
              环境配置
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <EnvInfo instance={instance} />
          </CardContent>
        </Card>
      )}

      {instance.env && Object.keys(instance.env).length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <VariableIcon className="w-4 h-4" />
              环境变量
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">键</th>
                    <th className="px-4 py-2.5 text-left font-medium">值</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(instance.env).map(([key, value]) => (
                    <tr key={key} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs">{key}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                        {String(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={VariableIcon}
              title="暂无环境变量"
              description="该沙箱实例尚未配置任何环境变量"
            />
          </CardContent>
        </Card>
      )}

      {(() => {
        const mounts = (instance.mountPoints ?? []).filter(
          (m) => m.type === MountPointType.OBJECT_STORAGE
        );
        if (mounts.length === 0) return null;
        return (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <DatabaseIcon className="w-4 h-4" />
                存储挂载
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium">Bucket</th>
                      <th className="px-4 py-2.5 text-left font-medium">子目录</th>
                      <th className="px-4 py-2.5 text-left font-medium">本地目录</th>
                      <th className="px-4 py-2.5 text-left font-medium">权限</th>
                      <th className="px-4 py-2.5 text-left font-medium">凭证 ID</th>
                      <th className="px-4 py-2.5 text-left font-medium">备注</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {mounts.map((mp, idx) => {
                      const secretUnavailable =
                        secretsLoaded && !!mp.secretId && !availableSecretIds.has(mp.secretId);
                      return (
                        <tr
                          key={idx}
                          className={`hover:bg-muted/30 transition-colors ${secretUnavailable ? 'bg-destructive/5' : ''}`}
                        >
                          <td className="px-4 py-2.5 font-mono text-xs">{mp.source || '-'}</td>
                          <td className="px-4 py-2.5 font-mono text-xs">{mp.subDir || '/'}</td>
                          <td className="px-4 py-2.5 font-mono text-xs">{mp.mountPath || '-'}</td>
                          <td className="px-4 py-2.5 text-xs">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${mp.readOnly ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}
                            >
                              {mp.readOnly ? '只读' : '读写'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground max-w-[160px]">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate">{mp.secretId || '-'}</span>
                              {secretUnavailable && (
                                <span
                                  title="该凭证已被删除或不可用"
                                  className="shrink-0 inline-flex items-center gap-1 text-destructive"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M12 9v3m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                    />
                                  </svg>
                                  <span className="text-xs font-medium">凭证不可用</span>
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground">
                            {mp.note || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}

function ToolsTab({
  toolsData,
  toolsLoading,
  onLoadTools,
  hasLoaded,
}: {
  toolsData: any;
  toolsLoading: boolean;
  onLoadTools: () => void;
  hasLoaded: boolean;
}) {
  if (!hasLoaded) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <ToolIcon className="w-6 h-6 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-medium mb-1">工具列表</h3>
            <p className="text-xs text-muted-foreground mb-4">点击下方按钮加载沙箱实例的工具列表</p>
            <Button onClick={onLoadTools} size="sm">
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              加载工具
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (toolsLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (toolsData?.data && toolsData.data.length > 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ToolIcon className="w-4 h-4" />
            工具列表
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">名称</th>
                  <th className="px-4 py-2.5 text-left font-medium">描述</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {toolsData.data.map((tool: SandboxTool, idx: number) => (
                  <tr key={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs">{tool.name}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">
                      {tool.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <EmptyState icon={ToolIcon} title="暂无工具" description="该沙箱实例尚未配置任何工具" />
      </CardContent>
    </Card>
  );
}

function TraceRecordsTab({
  traceRecordsData,
  traceRecordsLoading,
  traceRecordsPage,
  setTraceRecordsPage,
  traceRecordsPageSize,
  setTraceRecordsPageSize,
  timeRange,
  setTimeRange,
}: {
  traceRecordsData: any;
  traceRecordsLoading: boolean;
  traceRecordsPage: number;
  setTraceRecordsPage: (p: number) => void;
  traceRecordsPageSize: number;
  setTraceRecordsPageSize: (s: number) => void;
  timeRange: number | null;
  setTimeRange: (h: number | null) => void;
}) {
  const TIME_RANGES = [
    { label: '过去 3 小时', hours: 3 },
    { label: '过去 6 小时', hours: 6 },
    { label: '过去 12 小时', hours: 12 },
    { label: '过去 1 天', hours: 24 },
    { label: '过去 7 天', hours: 168 },
    { label: '过去 30 天', hours: 720 },
  ];

  if (traceRecordsLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const records = traceRecordsData?.data || [];
  const isEmpty = records.length === 0;

  const statusLabel = (status: number) => {
    switch (status) {
      case 1:
        return { text: '运行中', className: 'bg-blue-50 text-blue-600 border-blue-200' };
      case 2:
        return { text: '已完成', className: 'bg-green-50 text-green-600 border-green-200' };
      default:
        return { text: '未知', className: 'bg-gray-50 text-gray-600 border-gray-200' };
    }
  };

  const totalPages = Math.max(1, Math.ceil((traceRecordsData?.total || 0) / traceRecordsPageSize));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            运行记录
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">时间范围</span>
              <select
                value={timeRange ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setTimeRange(v ? Number(v) : null);
                  setTraceRecordsPage(1);
                }}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              >
                <option value="">全部</option>
                {TIME_RANGES.map((r) => (
                  <option key={r.hours} value={r.hours}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs text-muted-foreground">
              共 {traceRecordsData?.total || records.length} 条
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">
                  开始时间
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">
                  结束时间
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">
                  耗时
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">
                  状态
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">
                  沙箱 ID
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">
                  Trace ID
                </th>
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    暂无运行记录
                  </td>
                </tr>
              ) : (
                records.map((record: any, index: number) => {
                  const meta = statusLabel(record.status);
                  const durationMs = record.duration ?? 0;
                  const endTimeNano =
                    record.startTime && durationMs > 0
                      ? BigInt(record.startTime) + BigInt(Math.round(durationMs * 1e6))
                      : undefined;
                  return (
                    <tr key={index} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {record.startTime ? formatTimestamp(record.startTime) : '-'}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {endTimeNano !== undefined ? formatTimestamp(endTimeNano) : '-'}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {durationMs > 0 ? `${(durationMs / 1000).toFixed(1)}s` : '-'}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${meta.className}`}
                        >
                          {meta.text}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs max-w-[200px] truncate">
                        {record.sandboxId || '-'}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs max-w-[200px] truncate">
                        {record.traceId || '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">每页</span>
            <select
              value={traceRecordsPageSize}
              onChange={(e) => {
                setTraceRecordsPageSize(Number(e.target.value));
                setTraceRecordsPage(1);
              }}
              className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-xs text-muted-foreground">条</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={traceRecordsPage <= 1}
              onClick={() => setTraceRecordsPage(Math.max(1, traceRecordsPage - 1))}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-xs">
              <span className="font-medium">{traceRecordsPage}</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-muted-foreground">{totalPages}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={traceRecordsPage >= totalPages}
              onClick={() => setTraceRecordsPage(traceRecordsPage + 1)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SandboxInstanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hasLoadedTools, setHasLoadedTools] = useState(false);
  const [traceRecordsPage, setTraceRecordsPage] = useState(1);
  const [traceRecordsPageSize, setTraceRecordsPageSize] = useState(10);
  const [traceTimeRange, setTraceTimeRange] = useState<number | null>(null);

  const { data: instance, isLoading } = useQuery<Sandbox>({
    queryKey: ['sandbox', id],
    queryFn: () => sandboxApi.getSandbox(id!),
    enabled: !!id,
  });

  const {
    data: toolsData,
    isLoading: toolsLoading,
    refetch: refetchTools,
  } = useQuery({
    queryKey: ['sandbox-tools', id],
    queryFn: () => sandboxApi.listSandboxTools(id!),
    enabled: false,
  });

  const { data: traceRecordsData, isLoading: traceRecordsLoading } = useQuery({
    queryKey: ['sandbox-trace-records', id, traceRecordsPage, traceRecordsPageSize, traceTimeRange],
    queryFn: () => {
      const timestamps: { from?: string; to?: string } = {};
      if (traceTimeRange !== null) {
        const now = new Date();
        const from = new Date(now.getTime() - traceTimeRange * 60 * 60 * 1000);
        timestamps.from = from.toISOString();
        timestamps.to = now.toISOString();
      }
      return sandboxApi.listSandboxTraceRecords(
        create(ListSandboxTraceRecordsRequestSchema, {
          page: traceRecordsPage,
          pageSize: traceRecordsPageSize,
          fromTimestamp: timestamps.from ?? '',
          toTimestamp: timestamps.to ?? '',
          tags: [`user_tag_sandbox_id=${id!}`],
        })
      );
    },
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => sandboxApi.deleteSandbox(id!),
    onSuccess: () => navigate('/plugins/sandboxes?tab=instance'),
    onError: (error) => toast.error(`删除失败：${fullErrorText(error)}`),
  });

  const handleLoadTools = () => {
    setHasLoadedTools(true);
    refetchTools();
  };

  const tabs = [
    { id: 'overview' as TabId, label: '概览' },
    { id: 'tools' as TabId, label: '工具' },
    { id: 'trace-records' as TabId, label: '运行记录' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-destructive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">实例不存在</h3>
              <p className="text-muted-foreground mb-4">该沙箱实例可能已被删除或不存在</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/plugins/sandboxes')}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{instance.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {instance.description || '暂无描述'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/plugins/sandboxes/instances/${id}/edit`)}
          >
            <PencilIcon className="mr-1.5 h-3.5 w-3.5" />
            编辑
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            className="text-destructive hover:text-destructive"
          >
            <TrashIcon className="mr-1.5 h-3.5 w-3.5" />
            删除
          </Button>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={(id) => setActiveTab(id as TabId)} />

      <div className="animate-in fade-in duration-300">
        {activeTab === 'overview' && <OverviewTab instance={instance} />}
        {activeTab === 'tools' && (
          <ToolsTab
            toolsData={toolsData}
            toolsLoading={toolsLoading}
            onLoadTools={handleLoadTools}
            hasLoaded={hasLoadedTools}
          />
        )}
        {activeTab === 'trace-records' && (
          <TraceRecordsTab
            traceRecordsData={traceRecordsData}
            traceRecordsLoading={traceRecordsLoading}
            traceRecordsPage={traceRecordsPage}
            setTraceRecordsPage={setTraceRecordsPage}
            traceRecordsPageSize={traceRecordsPageSize}
            setTraceRecordsPageSize={setTraceRecordsPageSize}
            timeRange={traceTimeRange}
            setTimeRange={setTraceTimeRange}
          />
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除沙箱实例「{instance.name}」吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
